/**
 * Thin IndexedDB wrapper for the local sketch store.
 *
 * ADR 0007 chose IndexedDB over localStorage for the offline store —
 * asynchronous (never blocks the canvas), effectively unbounded, and structured
 * — and chose to own the small surface directly rather than pull in a wrapper
 * library. This is that surface: open the database, put a sketch, get a sketch.
 *
 * Everything here is browser-only and best-effort: persistence must never break
 * the app. If IndexedDB is unavailable (private mode, disabled, an old engine),
 * the calls resolve to a harmless no-op / null and the app runs in-memory, just
 * without surviving a refresh. The pure serialize/hydrate logic lives in
 * sketch-store.js and is unit-tested there; this layer is verified live.
 */

const DB_NAME = 'room-sketch';
const DB_VERSION = 4;
const STORE = 'sketches';
const META_STORE = 'meta'; // small key/value store: which sketch was last open
// Kept separate from sketches because a field session is reviewable input, not
// tablet geometry. A phone sync must never replace an open room document.
const MEASUREMENT_SESSION_STORE = 'measurement_sessions';
const REMEASURE_SESSION_STORE = 'remeasure_sessions';
const FALLBACK_SKETCH_PREFIX = 'room-sketch:fallback:sketch:';
const FALLBACK_META_PREFIX = 'room-sketch:fallback:meta:';
const DB_OPEN_TIMEOUT_MS = 1200;
const DB_OPERATION_TIMEOUT_MS = 1200;

let dbPromise = null;

function storage() {
  try { return typeof localStorage !== 'undefined' ? localStorage : null; }
  catch { return null; }
}

function fallbackSketchKey(id) { return `${FALLBACK_SKETCH_PREFIX}${encodeURIComponent(String(id))}`; }
function fallbackMetaKey(key) { return `${FALLBACK_META_PREFIX}${encodeURIComponent(String(key))}`; }

/**
 * A small recovery shadow for sketches whose IndexedDB open/transaction is
 * blocked by a stale tablet tab. IndexedDB remains primary; this only prevents
 * Save/Open from hanging forever on the exact device that most needs offline
 * persistence. Each sketch is a separate key so one large sketch cannot make
 * every other recovery record unreadable.
 */
function putFallbackSketch(doc) {
  const target = storage();
  if (!target || !doc?.id) return false;
  try { target.setItem(fallbackSketchKey(doc.id), JSON.stringify(doc)); return true; }
  catch { return false; }
}

function getFallbackSketch(id) {
  const target = storage();
  if (!target) return null;
  try {
    const raw = target.getItem(fallbackSketchKey(id));
    const doc = raw ? JSON.parse(raw) : null;
    return doc && typeof doc === 'object' && doc.id ? doc : null;
  } catch { return null; }
}

function listFallbackSketches() {
  const target = storage();
  if (!target) return [];
  const docs = [];
  try {
    for (let i = 0; i < target.length; i += 1) {
      const key = target.key(i);
      if (!key?.startsWith(FALLBACK_SKETCH_PREFIX)) continue;
      const doc = JSON.parse(target.getItem(key));
      if (doc && typeof doc === 'object' && doc.id) docs.push(doc);
    }
  } catch { /* return every record decoded before the bad one */ }
  return docs;
}

function deleteFallbackSketch(id) {
  const target = storage();
  if (!target) return false;
  try {
    const key = fallbackSketchKey(id);
    const existed = target.getItem(key) !== null;
    target.removeItem(key);
    return existed;
  } catch { return false; }
}

function setFallbackMeta(key, value) {
  const target = storage();
  if (!target) return false;
  try { target.setItem(fallbackMetaKey(key), JSON.stringify(value)); return true; }
  catch { return false; }
}

function getFallbackMeta(key) {
  const target = storage();
  if (!target) return null;
  try {
    const raw = target.getItem(fallbackMetaKey(key));
    return raw === null ? null : JSON.parse(raw);
  } catch { return null; }
}

function timebox(promise, fallback, timeoutMs = DB_OPERATION_TIMEOUT_MS) {
  return new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(value);
    };
    const timer = setTimeout(() => finish(fallback), timeoutMs);
    Promise.resolve(promise).then(finish, () => finish(fallback));
  });
}

/**
 * Ask the browser to make this origin's storage PERSISTENT — i.e. exempt from
 * the automatic eviction that can clear IndexedDB under disk pressure. Without
 * this, a busy tablet could quietly drop a saved sketch; with it granted, the
 * browser keeps the data until the user explicitly clears site data.
 *
 * Best-effort: returns the granted state, or false if the API is unavailable.
 * Safe to call more than once. Some browsers grant it automatically for
 * installed/frequently-used sites and may prompt or defer otherwise.
 */
export async function requestPersistentStorage() {
  try {
    if (typeof navigator === 'undefined' || !navigator.storage) return false;
    if (typeof navigator.storage.persisted === 'function' && (await navigator.storage.persisted())) {
      return true;
    }
    if (typeof navigator.storage.persist === 'function') {
      return await navigator.storage.persist();
    }
    return false;
  } catch {
    return false;
  }
}

function hasIndexedDb() {
  return typeof indexedDB !== 'undefined' && indexedDB !== null;
}

function openDatabase() {
  if (!hasIndexedDb()) return Promise.resolve(null);
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve) => {
    let request;
    let settled = false;
    let timer = null;
    const finish = (db) => {
      if (settled) {
        // A late success after the timeout must not leave an unused connection
        // holding future upgrades open.
        try { db?.close?.(); } catch { /* best effort */ }
        return;
      }
      settled = true;
      if (timer !== null) clearTimeout(timer);
      resolve(db);
    };
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      finish(null);
      return;
    }
    timer = setTimeout(() => finish(null), DB_OPEN_TIMEOUT_MS);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
      // Added in v2. Guarded so upgrading a v1 database (which already has
      // `sketches`) only creates the missing `meta` store.
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(MEASUREMENT_SESSION_STORE)) {
        db.createObjectStore(MEASUREMENT_SESSION_STORE, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(REMEASURE_SESSION_STORE)) {
        db.createObjectStore(REMEASURE_SESSION_STORE, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => finish(request.result);
    // A failed open is not fatal — fall back to in-memory.
    request.onerror = () => finish(null);
    request.onblocked = () => finish(null);
  });
  return dbPromise;
}

function putSketchInDatabase(doc) {
  return openDatabase().then((db) => {
    if (!db) return false;
    return timebox(new Promise((resolve) => {
      try {
        const tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).put(doc);
        tx.oncomplete = () => resolve(true);
        tx.onerror = () => resolve(false);
        tx.onabort = () => resolve(false);
      } catch { resolve(false); }
    }), false);
  });
}

/**
 * Persists a sketch document (keyed by its `id`). Resolves true on success,
 * false when persistence was unavailable or failed — callers treat a false as
 * "not saved this time", never as an error to surface mid-sketch.
 */
export async function putSketch(doc) {
  const databaseSaved = await putSketchInDatabase(doc);
  if (databaseSaved) {
    deleteFallbackSketch(doc?.id);
    return true;
  }
  // Only pay localStorage's synchronous serialization cost when IndexedDB is
  // actually unavailable/stalled; healthy tablets remain IndexedDB-only.
  return putFallbackSketch(doc);
}

/** Reads a sketch document by id, or null if absent/unavailable. */
export async function getSketch(id) {
  const fallback = getFallbackSketch(id);
  if (fallback) return fallback;
  const db = await openDatabase();
  if (!db) return getFallbackSketch(id);
  const stored = await timebox(new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readonly');
      const request = tx.objectStore(STORE).get(id);
      request.onsuccess = () => resolve(request.result ?? null);
      request.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  }), null);
  return stored ?? getFallbackSketch(id);
}

/** All stored sketch documents (full), or [] if unavailable. */
export async function listSketches() {
  const db = await openDatabase();
  if (!db) return listFallbackSketches();
  const stored = await timebox(new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readonly');
      const request = tx.objectStore(STORE).getAll();
      request.onsuccess = () => resolve(request.result ?? []);
      request.onerror = () => resolve([]);
    } catch {
      resolve([]);
    }
  }), []);
  const fallback = listFallbackSketches();
  const merged = new Map(stored.map((doc) => [doc.id, doc]));
  for (const doc of fallback) merged.set(doc.id, doc);
  return [...merged.values()];
}

/** Deletes a sketch by id. Resolves true on success. */
export async function deleteSketch(id) {
  const fallbackDeleted = deleteFallbackSketch(id);
  const db = await openDatabase();
  if (!db) return fallbackDeleted;
  const databaseDeleted = await new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(id);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
      tx.onabort = () => resolve(false);
    } catch {
      resolve(false);
    }
  });
  return databaseDeleted || fallbackDeleted;
}

/**
 * Persists a field-measurement session separately from its room sketch. This
 * boundary is intentional: tablet geometry stays untouched until a designer
 * reviews and applies the session.
 */
export async function putMeasurementSession(session) {
  return putDocument(MEASUREMENT_SESSION_STORE, session);
}

/** Reads a field-measurement session by id, or null if absent/unavailable. */
export async function getMeasurementSession(id) {
  return getDocument(MEASUREMENT_SESSION_STORE, id);
}

/** All field-measurement sessions, or [] when IndexedDB is unavailable. */
export async function listMeasurementSessions() {
  return listDocuments(MEASUREMENT_SESSION_STORE);
}

/** Deletes one field-measurement session. */
export async function deleteMeasurementSession(id) {
  return deleteDocument(MEASUREMENT_SESSION_STORE, id);
}

/** Persists the immutable-original + separate Actual remeasure document. */
export async function putRemeasureSession(session) { return putDocument(REMEASURE_SESSION_STORE, session); }

/** Reads one remeasure session by id. */
export async function getRemeasureSession(id) { return getDocument(REMEASURE_SESSION_STORE, id); }

/** All remeasure sessions, including completed Engineering records. */
export async function listRemeasureSessions() { return listDocuments(REMEASURE_SESSION_STORE); }

function putDocument(storeName, doc) {
  return openDatabase().then((db) => {
    if (!db) return false;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(storeName, 'readwrite');
        tx.objectStore(storeName).put(doc);
        tx.oncomplete = () => resolve(true);
        tx.onerror = () => resolve(false);
        tx.onabort = () => resolve(false);
      } catch { resolve(false); }
    });
  });
}

function getDocument(storeName, id) {
  return openDatabase().then((db) => {
    if (!db) return null;
    return new Promise((resolve) => {
      try {
        const request = db.transaction(storeName, 'readonly').objectStore(storeName).get(id);
        request.onsuccess = () => resolve(request.result ?? null);
        request.onerror = () => resolve(null);
      } catch { resolve(null); }
    });
  });
}

function listDocuments(storeName) {
  return openDatabase().then((db) => {
    if (!db) return [];
    return new Promise((resolve) => {
      try {
        const request = db.transaction(storeName, 'readonly').objectStore(storeName).getAll();
        request.onsuccess = () => resolve(request.result ?? []);
        request.onerror = () => resolve([]);
      } catch { resolve([]); }
    });
  });
}

function deleteDocument(storeName, id) {
  return openDatabase().then((db) => {
    if (!db) return false;
    return new Promise((resolve) => {
      try {
        const tx = db.transaction(storeName, 'readwrite');
        tx.objectStore(storeName).delete(id);
        tx.oncomplete = () => resolve(true);
        tx.onerror = () => resolve(false);
        tx.onabort = () => resolve(false);
      } catch { resolve(false); }
    });
  });
}

/** Stores a small key/value pointer (e.g. the last-opened sketch id). */
export async function setMeta(key, value) {
  const fallbackSaved = setFallbackMeta(key, value);
  const db = await openDatabase();
  if (!db) return fallbackSaved;
  const databaseSaved = await new Promise((resolve) => {
    try {
      const tx = db.transaction(META_STORE, 'readwrite');
      tx.objectStore(META_STORE).put({ key, value });
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
      tx.onabort = () => resolve(false);
    } catch {
      resolve(false);
    }
  });
  return databaseSaved || fallbackSaved;
}

/**
 * Reads a key/value pointer, distinguishing ABSENT from FAILED.
 *
 * `getMeta` cannot: every failure path in it — no storage at all, an
 * IndexedDB open blocked by a stale tab, a transaction that throws, a request
 * that errors — resolves to `null`, which is the same value a key that was
 * never written returns. For a pointer like `lastOpenedId` that conflation is
 * harmless: the caller opens nothing either way.
 *
 * For a COLLECTION it destroys data. A caller that reads null, treats it as an
 * empty collection, appends one item and writes the result back has replaced
 * the whole stored collection with that one item — on a device that still had
 * the rest a moment earlier. That is what happened to the reusable-section
 * library (ADR 0100), and no error was swallowed on the way: there was never
 * an error to swallow, only a null.
 *
 * @returns {Promise<{ok: true, value: *} | {ok: false, reason: string}>}
 *   `ok: true, value: null` means the key is genuinely absent.
 */
export async function getMetaResult(key) {
  const fallback = getFallbackMeta(key);
  if (fallback !== null) return { ok: true, value: fallback };
  if (!hasIndexedDb()) {
    // No shadow value and no database. This is not an empty key — it is a
    // device that cannot answer the question, and a caller must not write a
    // collection back over one it never read.
    return { ok: false, reason: 'This browser has no available local storage.' };
  }
  const db = await openDatabase();
  if (!db) return { ok: false, reason: 'The local database could not be opened — another tab may be holding it.' };
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(META_STORE, 'readonly');
      const request = tx.objectStore(META_STORE).get(key);
      request.onsuccess = () => resolve({ ok: true, value: request.result ? request.result.value : null });
      request.onerror = () => resolve({ ok: false, reason: 'The local database could not be read.' });
    } catch (cause) {
      resolve({ ok: false, reason: cause instanceof Error ? cause.message : 'The local database could not be read.' });
    }
  });
}

/** Reads a key/value pointer, or null. Cannot tell absent from failed — for
 *  anything a caller would WRITE BACK, use `getMetaResult`. */
export async function getMeta(key) {
  const fallback = getFallbackMeta(key);
  if (fallback !== null) return fallback;
  const db = await openDatabase();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(META_STORE, 'readonly');
      const request = tx.objectStore(META_STORE).get(key);
      request.onsuccess = () => resolve(request.result ? request.result.value : null);
      request.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}
